import { Alert, Form, Select, Spin, Typography } from 'antd';
import { get } from 'lodash';
import React, { useState } from 'react';
import { useQuery } from 'react-apollo';
import { NOTE_TYPES } from '../note-form/fields';
import {
  ADMIN_INVESTMENT_WALLET,
  GET_INVESTMENTS,
} from '../../pages/apollo_queries';
import { fmtMoney } from '../investment-rules/shared';
import { MovementModal } from './MovementModal';

// A withdrawal started from the Treasury page. ONE modal (TL, 2026-09-18: two steps read as the process
// restarting): the first box is WHERE the money leaves from — the house's own wallet, or the wallet of a
// note, listed under its PRODUCT the way the dashboard sells them — and the rest is the shared form.
const TREASURY = 'treasury';

// The dashboard's product groups, in its order. Demand is kept visible under Constitution Notes.
const GROUPS = [
  { label: 'Individual Notes', types: [NOTE_TYPES.individual] },
  { label: 'Credit Fund', types: [NOTE_TYPES.creditFund] },
  {
    label: 'Constitution Notes',
    types: [NOTE_TYPES.constitution, NOTE_TYPES.rolling, NOTE_TYPES.demand],
  },
];

export function TreasuryWithdrawModal({
  banks,
  treasuryAvailable,
  onClose,
  onDone,
}) {
  const [source, setSource] = useState(TREASURY);
  const isNote = source !== TREASURY;

  // The whole catalogue once (~100 notes in prod), grouped on the client; the search is by name.
  const { data: notesData, loading: notesLoading } = useQuery(GET_INVESTMENTS, {
    variables: { page: 1, count: 500, sortBy: 'Address', sortDirection: 'ASC' },
  });
  const {
    data: walletData,
    loading: walletLoading,
    error: walletError,
  } = useQuery(ADMIN_INVESTMENT_WALLET, {
    variables: { investmentId: source },
    skip: !isNote,
    fetchPolicy: 'network-only',
  });

  const notes = get(notesData, 'getInvestments', []);
  const noteWallet = get(walletData, 'adminInvestmentWallet', null);
  const available = isNote
    ? noteWallet
      ? noteWallet.availableBalance
      : null
    : treasuryAvailable;
  const noteUnusable = isNote && !walletLoading && (walletError || !noteWallet);

  const topSlot = (
    <Form layout="vertical" style={{ marginBottom: 0 }}>
      <Form.Item label="From" style={{ marginBottom: 8 }}>
        <Select
          showSearch
          optionFilterProp="children"
          value={source}
          onChange={setSource}
          loading={notesLoading}
        >
          <Select.OptGroup label="Treasury">
            <Select.Option value={TREASURY}>
              Constitution's wallet
              {typeof treasuryAvailable === 'number' &&
                ` · ${fmtMoney(treasuryAvailable)} available`}
            </Select.Option>
          </Select.OptGroup>
          {GROUPS.map((group) => {
            const items = notes.filter((note) =>
              group.types.includes(note.noteType)
            );
            return items.length ? (
              <Select.OptGroup key={group.label} label={group.label}>
                {items.map((note) => (
                  <Select.Option key={note._id} value={note._id}>
                    {note.address || note._id}
                  </Select.Option>
                ))}
              </Select.OptGroup>
            ) : null;
          })}
        </Select>
      </Form.Item>
      {/* The selected source's money, right under the choice (TL, 2026-09-29): read live from Sila, so
          the operator sees what can leave BEFORE typing an amount. */}
      <div style={{ marginBottom: 16, minHeight: 56 }}>
        {isNote && walletLoading ? (
          <Spin size="small" tip="Reading the note's wallet from Sila…" />
        ) : noteUnusable ? (
          <Alert
            type="warning"
            showIcon
            message="This note has no wallet yet, or Sila could not be reached"
            description="Nothing can leave from it, so the form is blocked. Pick another source."
          />
        ) : (
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              justifyContent: 'space-between',
              padding: '8px 12px',
              background: '#fafafa',
              border: '1px solid #f0f0f0',
              borderRadius: 4,
            }}
          >
            <Typography.Text type="secondary">
              {isNote
                ? 'Available in this note’s wallet'
                : 'Available in Constitution’s wallet'}
            </Typography.Text>
            <Typography.Text strong style={{ fontSize: 18 }}>
              {typeof available === 'number' ? fmtMoney(available) : '—'}
            </Typography.Text>
          </div>
        )}
        {isNote && noteWallet && (
          <Typography.Text
            type="secondary"
            style={{ display: 'block', marginTop: 6, fontSize: 12 }}
          >
            The money investors put into this note, leaving its wallet to a bank
            of Constitution.
          </Typography.Text>
        )}
      </div>
    </Form>
  );

  return (
    <MovementModal
      direction={isNote ? 'noteWithdraw' : 'withdraw'}
      banks={banks}
      extra={isNote ? { investmentId: source } : {}}
      available={available}
      topSlot={topSlot}
      sourceLabel={
        isNote
          ? (notes.find((note) => note._id === source) || {}).address
          : undefined
      }
      okDisabled={isNote && (walletLoading || !!noteUnusable)}
      onClose={onClose}
      onDone={onDone}
    />
  );
}
